import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { ReservationSlotTemplate } from '../../../../domain/reservationSlots';
import type { UseReservationSlotSettingsReturn } from '../hooks/useReservationSlotSettings';
import { doctorApiService } from '../../../../services/doctorApiService';
import type { Doctor } from '../../../../types/doctor';
import {
  LoadingOverlay,
  OverrideRowEdit,
  AddRowButton,
  Callout,
} from '../ReservationSlotManagement.styles';
import { formatTemplateLabel } from '../formatTemplateLabel';

/**
 * 医師一覧APIの1ページあたり取得件数
 *
 * @description
 * バックエンドの `DoctorIndexRequest` が `per_page` を最大100に制限しているため、
 * これを超える値を送ると422になる。全医師を得るにはページを送って繰り返し取得する。
 */
const DOCTORS_PER_PAGE = 100;

export interface SettingsPanelProps {
  templates: ReservationSlotTemplate[];
  settings: UseReservationSlotSettingsReturn;
}

/**
 * デフォルトテンプレート選択の強調装飾（青）
 *
 * @description
 * 「全医師に効く設定」であることを一目で分かるようにするための装飾。
 * モックの `border-color:var(--blue);color:var(--blue-d);font-weight:700` に対応する。
 */
const defaultTemplateSelectSx: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  color: theme.palette.primary.dark,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-icon': {
    color: theme.palette.primary.main,
  },
});

/**
 * 個別上書きのテンプレート選択の強調装飾（琥珀）
 *
 * @description
 * デフォルトから外れた設定であることを示すための装飾。
 * モックの `border-color:var(--amber);background:var(--amber-s);color:var(--amber);font-weight:700` に対応する。
 */
const overrideTemplateSelectSx: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  color: theme.palette.warning.dark,
  backgroundColor: alpha(theme.palette.warning.main, 0.12),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.warning.main,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.warning.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.warning.main,
  },
  '& .MuiSelect-icon': {
    color: theme.palette.warning.main,
  },
});

/**
 * 医師の表示名を生成する（姓 名）
 *
 * @param {Doctor} doctor - 対象の医師
 * @returns {string} 表示名
 */
function doctorDisplayName(doctor: Doctor): string {
  return `${doctor.first_name} ${doctor.last_name}`.trim();
}

/**
 * テンプレートIDからテンプレート名を引く
 *
 * @param {ReservationSlotTemplate[]} templates - テンプレート一覧
 * @param {string} id - テンプレートID
 * @returns {string} テンプレート名（見つからない場合は「（不明なテンプレート）」）
 */
function templateName(templates: ReservationSlotTemplate[], id: string): string {
  return templates.find((t) => t.id === id)?.name ?? '（不明なテンプレート）';
}

/**
 * 基本設定パネル
 *
 * @description
 * 全医師に適用するデフォルトテンプレートと、医師個別の上書き設定を編集する。
 * 個別上書きは「行を追加して行内で医師とテンプレートを選ぶ」方式で、
 * 1人の医師が複数行に登場しないよう選択肢を絞り込む。
 * 保存時に overrides は全置換される。
 *
 * 保存操作は本パネルの外側（タブの見出し行・ダイアログのフッター）が持つ。
 */
const SettingsPanel: React.FC<SettingsPanelProps> = ({ templates, settings }) => {
  const {
    defaultTemplateId,
    overrides,
    isLoading,
    error,
    successMessage,
    setDefaultTemplate,
    addOverride,
    updateOverrideTemplate,
    replaceOverrideDoctor,
    removeOverride,
    clearError,
    clearSuccess,
  } = settings;

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setDoctorsLoading(true);
      setDoctorsError(null);
      try {
        // 全医師を得るため、最終ページに達するまでページを送って取得する
        const allDoctors: Doctor[] = [];
        let currentPage = 1;
        let hasMorePages = true;

        while (hasMorePages && active) {
          const response = await doctorApiService.getDoctors({
            page: currentPage,
            per_page: DOCTORS_PER_PAGE,
          });
          allDoctors.push(...response.data);

          if (response.meta && response.meta.current_page < response.meta.last_page) {
            currentPage += 1;
          } else {
            hasMorePages = false;
          }
        }

        if (active) {
          setDoctors(allDoctors);
        }
      } catch (e) {
        if (active) {
          setDoctorsError(e instanceof Error ? e.message : '医師一覧の取得に失敗しました');
        }
      } finally {
        if (active) {
          setDoctorsLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // まだどの上書き行にも使われていない医師（各行の選択肢と行追加の候補に使う）
  const availableDoctors = useMemo(() => {
    const overriddenIds = new Set(overrides.map((o) => o.doctorId));
    return doctors.filter((d) => !overriddenIds.has(d.id));
  }, [doctors, overrides]);

  const canAddOverride = availableDoctors.length > 0 && templates.length > 0;

  /**
   * 上書き行を1行追加する（未使用医師の先頭＋デフォルトテンプレート）
   */
  const handleAddOverrideRow = useCallback(() => {
    const doctor = availableDoctors[0];
    if (!doctor || templates.length === 0) {
      return;
    }
    const templateId =
      defaultTemplateId && templates.some((t) => t.id === defaultTemplateId)
        ? defaultTemplateId
        : templates[0].id;
    addOverride(doctor.id, doctorDisplayName(doctor), templateId);
  }, [availableDoctors, templates, defaultTemplateId, addOverride]);

  /**
   * 上書き行の対象医師を差し替える
   *
   * @param {number} currentDoctorId - 現在その行に設定されている医師ID
   * @param {number} nextDoctorId - 差し替え先の医師ID
   */
  const handleReplaceDoctor = useCallback(
    (currentDoctorId: number, nextDoctorId: number) => {
      const doctor = doctors.find((d) => d.id === nextDoctorId);
      if (!doctor) {
        return;
      }
      replaceOverrideDoctor(currentDoctorId, nextDoctorId, doctorDisplayName(doctor));
    },
    [doctors, replaceOverrideDoctor]
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {(isLoading || doctorsLoading) && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      {doctorsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDoctorsError(null)}>
          {doctorsError}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>
          {successMessage}
        </Alert>
      )}

      {/* デフォルトテンプレート */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        デフォルトテンプレート
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        個別上書きのない医師全員に適用されるデフォルトのテンプレートです。新しく医師が増えたときも自動で適用されます。
      </Typography>
      <FormControl size="small" sx={{ minWidth: 260, mb: 3 }}>
        <InputLabel>デフォルトテンプレート</InputLabel>
        <Select
          label="デフォルトテンプレート"
          value={defaultTemplateId ?? ''}
          onChange={(e) => setDefaultTemplate(e.target.value === '' ? null : e.target.value)}
          sx={defaultTemplateSelectSx}
        >
          <MenuItem value="">
            <em>未設定</em>
          </MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {formatTemplateLabel(template)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ mb: 2 }} />

      {/* 医師個別の上書き */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        医師個別の上書き
      </Typography>

      {/* 上書き行の一覧（行内で医師とテンプレートを選ぶ） */}
      {overrides.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          個別上書きはありません。
        </Typography>
      ) : (
        <Box>
          {overrides.map((override) => {
            const selfDoctor = doctors.find((d) => d.id === override.doctorId);
            const isKnownTemplate = templates.some((t) => t.id === override.templateId);

            return (
              <OverrideRowEdit key={override.doctorId}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={String(override.doctorId)}
                    onChange={(e) =>
                      handleReplaceDoctor(override.doctorId, Number(e.target.value))
                    }
                    inputProps={{ 'aria-label': '医師' }}
                  >
                    {/* 自分自身（医師一覧から引けない場合は保存済みの表示名で代替） */}
                    <MenuItem value={String(override.doctorId)}>
                      {selfDoctor ? doctorDisplayName(selfDoctor) : override.doctorName}
                    </MenuItem>
                    {availableDoctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={String(doctor.id)}>
                        {doctorDisplayName(doctor)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <Select
                    value={isKnownTemplate ? override.templateId : ''}
                    onChange={(e) => updateOverrideTemplate(override.doctorId, e.target.value)}
                    displayEmpty
                    inputProps={{ 'aria-label': 'テンプレート' }}
                    sx={overrideTemplateSelectSx}
                  >
                    {!isKnownTemplate && (
                      <MenuItem value="">
                        <em>{templateName(templates, override.templateId)}</em>
                      </MenuItem>
                    )}
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {formatTemplateLabel(template)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Tooltip title="削除">
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="この上書きを削除"
                    onClick={() => removeOverride(override.doctorId)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </OverrideRowEdit>
            );
          })}
        </Box>
      )}

      <AddRowButton onClick={handleAddOverrideRow} disabled={!canAddOverride}>
        ＋ 個別上書きする医師を追加する
      </AddRowButton>

      <Callout $tone="blue" sx={{ mt: 1.5 }}>
        <Box component="span" sx={{ fontWeight: 800, flexShrink: 0 }}>
          💡
        </Box>
        <Box component="div">
          基本設定のテンプレートを変更しても、個別上書きに登録した医師には影響しません。
        </Box>
      </Callout>
    </Box>
  );
};

export default SettingsPanel;
